import React, { useState, useEffect, useCallback } from "react";
import { format, startOfMonth } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import type { DayContentProps } from "react-day-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { OrderSummaryDaily } from "@/types/order-type";
import { getSummaryOrderList } from "@/api/order-api";
import { toast } from "@/hooks/use-toast";

type OrderCountData = {
  [date: string]: OrderSummaryDaily;
};

interface OrderDatePickerProps {
  date?: Date | undefined | null;
  onDateChange: (date: Date | undefined) => void;
  className?: string;
  placeholder?: string;
}
export function OrderDatePicker({
  date,
  onDateChange,
  className,
  placeholder = "Select date",
}: OrderDatePickerProps) {
  const [orderCounts, setOrderCounts] = useState<OrderCountData>({});
  const [open, setOpen] = useState(false);
  const [mouseOverKey, setMouseOverKey] = useState<string | null | undefined>(
    null
  );
  const [currentOrderCount, setCurrentOrderCount] =
    useState<OrderSummaryDaily | null>(null);
  const [calandarDate, setCalandarDate] = useState<Date>(date ?? new Date());
  const [displayMonth, setDisplayMonth] = useState<Date>(
    () => startOfMonth(date ?? new Date())
  );
  const calandarDateRef = React.useRef(calandarDate);
  calandarDateRef.current = calandarDate;

  useEffect(() => {
    if (!date) {
      const today = new Date();
      setCalandarDate(today);
      onDateChange(today);
    }
  }, [date, onDateChange]);

  const getFromDateToDate = useCallback((monthDate: Date) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    const fmt = (d: Date) => d.toISOString().split("T")[0];
    return {
      startDate: fmt(start),
      endDate: fmt(end),
    };
  }, []);

  const transformOrderSummaryList = useCallback(
    (orderSummaries: OrderSummaryDaily[]) => {
      const result: OrderCountData = {};
      orderSummaries.forEach((item) => {
        const raw =
          (item as OrderSummaryDaily & { date?: string }).summaryDate ??
          (item as OrderSummaryDaily & { date?: string }).date;
        if (raw) {
          const dateKey =
            typeof raw === "string" && raw.length >= 10 ? raw.slice(0, 10) : raw;
          result[dateKey] = item;
        }
      });
      return result;
    },
    []
  );

  const fetchOrderSummaryList = useCallback(async () => {
    const { startDate, endDate } = getFromDateToDate(displayMonth);
    const resp = await getSummaryOrderList(startDate, endDate);
    if (resp.success) {
      const raw = resp.data;
      const list: OrderSummaryDaily[] = Array.isArray(raw)
        ? raw
        : (raw as unknown as { content?: OrderSummaryDaily[] })?.content ?? [];
      const listSummary = transformOrderSummaryList(list);
      setOrderCounts(listSummary);
      const dateKey = format(calandarDateRef.current, "yyyy-MM-dd");
      setCurrentOrderCount(listSummary[dateKey] ?? null);
    } else {
      toast({
        description: resp.message,
        variant: "destructive",
        title: "Error",
      });
    }
  }, [displayMonth, getFromDateToDate, transformOrderSummaryList]);

  useEffect(() => {
    fetchOrderSummaryList();
  }, [fetchOrderSummaryList]);

  useEffect(() => {
    if (open && calandarDate) {
      const nextMonth = startOfMonth(calandarDate);
      setDisplayMonth(nextMonth);
    }
  }, [open, calandarDate]);

  useEffect(() => {
    if (mouseOverKey && orderCounts[mouseOverKey]) {
      setCurrentOrderCount(orderCounts[mouseOverKey]);
    } else {
      const dateKey = calandarDate ? format(calandarDate, "yyyy-MM-dd") : "";
      setCurrentOrderCount(dateKey ? orderCounts[dateKey] ?? null : null);
    }
  }, [mouseOverKey, orderCounts, calandarDate]);

  const DayContent = (props: DayContentProps) => {
    const dateKey = format(props.date, "yyyy-MM-dd");
    const counts = orderCounts[dateKey];
    const hasOrders = counts && counts.totalOrders > 0;
    let dotClass = "";
    if (counts?.totalPaidAmount > 0) {
      dotClass = "bg-emerald-500";
    } else if (counts?.totalConfirmed > 0) {
      dotClass = "bg-blue-500";
    } else if (counts?.totalOrders > 0) {
      dotClass = "bg-slate-400";
    }

    const title = hasOrders
      ? `Orders: ${counts!.totalOrders}, Confirmed: ${counts!.totalConfirmed
      }, Paid: ${counts!.totalPaidAmount}`
      : undefined;

    return (
      <div
        className={cn(
          "relative flex h-9 w-9 items-center justify-center",
          "cursor-pointer select-none"
        )}
        title={title}
        onMouseEnter={() => setMouseOverKey(dateKey)}
        onMouseLeave={() => setMouseOverKey(null)}
      >
        {/* day number (use children if you want, here we use date.getDate() directly) */}
        <span>{props.date.getDate()}</span>

        {/* dot indicator */}
        {hasOrders && (
          <span
            className={cn(
              "absolute bottom-1 h-1.5 w-1.5 rounded-full",
              dotClass
            )}
          />
        )}
      </div>
    );
  };

  return (
    <div className="mt-[7px]">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !calandarDate && "text-muted-foreground",
              className
            )}
          >
            <div className="flex flex-col w-full gap-1">
              {/* Top row: icon + date + total */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {calandarDate ? (
                    <span>{format(calandarDate, " dd/MM/yyyy")}</span>
                  ) : (
                    <span>{placeholder}</span>
                  )}
                </div>

                <div className="flex items-center gap-1 text-[11px] text-slate-700">
                  <span
                    className="inline-flex items-center justify-center px-2 py-0.5 rounded-full 
                       bg-slate-100 text-slate-700 border border-slate-200"
                  >
                    {currentOrderCount?.totalOrders ?? 0}
                  </span>
                </div>
              </div>
            </div>
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={calandarDate}
            defaultMonth={displayMonth}
            onMonthChange={(month) => setDisplayMonth(startOfMonth(month))}
            onSelect={(newDate) => {
              onDateChange(newDate);
              if (newDate) {
                setCalandarDate(newDate);
                setDisplayMonth(startOfMonth(newDate));
                setOpen(false);
              }
            }}
            components={{
              DayContent,
            }}
          />

          {/* Legend */}
          <div className="p-3 border-t text-xs flex gap-3">
            <div className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
              <span>
                Has orders{" "}
                {currentOrderCount && ` : ${currentOrderCount.totalOrders}`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              <span>
                Confirmed{" "}
                {currentOrderCount && ` : ${currentOrderCount.totalConfirmed}`}{" "}
                {!currentOrderCount && ` `}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span>
                Paid {currentOrderCount && ` : ${currentOrderCount.totalPaidAmount}`}{" "}
                {!currentOrderCount && ` `}
              </span>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
